import type { ReactNode } from 'react';
import { LandingIcon } from '../../ui/LandingIcon.js';

type Tx = (en: string, tr: string) => string;

/**
 * The theme card's picture: a product page on the Krevya Theme, drawn in the
 * page's one blue. Line art rather than a screenshot, because a screenshot of
 * one store would read as that store's design, and the card is selling the
 * structure every store gets: the gallery, the offer tiers with one chosen,
 * the button, and the sections stacked underneath.
 */
export function ThemeArt() {
  const star = (x: number) =>
    `M${x} 98l3 6 6 1-4.5 4 1 6-5.5-3-5.5 3 1-6-4.5-4 6-1z`;
  return (
    <figure className="lp-theme-art" aria-hidden="true">
      <svg viewBox="0 0 360 280" focusable="false">
        <rect className="lp-theme-art-paper" x="8" y="8" width="344" height="264" rx="18" />
        <path d="M8 40h344" />
        <path className="lp-theme-art-dots" d="M30 24h.01M44 24h.01M58 24h.01" />
        <rect className="lp-theme-art-tint" x="118" y="18" width="124" height="12" rx="6" />
        {/* The gallery: one large frame and its thumbnails. */}
        <rect className="lp-theme-art-tint" x="30" y="58" width="138" height="120" rx="12" />
        <path d="M54 156l28-32 18 20 14-14 30 26" />
        <circle cx="134" cy="86" r="9" />
        <rect x="30" y="188" width="40" height="30" rx="7" />
        <rect x="79" y="188" width="40" height="30" rx="7" />
        <rect x="128" y="188" width="40" height="30" rx="7" />
        {/* Title, stars and price. */}
        <path d="M190 64h112M190 80h70" />
        <path className="lp-theme-art-fine" d={`${star(196)}${star(218)}${star(240)}`} />
        {/* Three offer tiers, the middle one chosen. */}
        <rect x="190" y="124" width="140" height="24" rx="8" />
        <circle cx="204" cy="136" r="5" />
        <path d="M218 136h56" />
        <rect className="lp-theme-art-chosen" x="190" y="154" width="140" height="24" rx="8" />
        <circle cx="204" cy="166" r="5" />
        <circle className="lp-theme-art-solid" cx="204" cy="166" r="2.2" />
        <path d="M218 166h72" />
        <rect x="190" y="184" width="140" height="24" rx="8" />
        <circle cx="204" cy="196" r="5" />
        <path d="M218 196h46" />
        <rect className="lp-theme-art-solid" x="190" y="216" width="140" height="22" rx="11" />
        {/* The sections that follow, under the fold. */}
        <path className="lp-theme-art-fine" d="M30 246h300" />
        <path d="M30 260h64M148 260h64M266 260h64" />
      </svg>
    </figure>
  );
}

/*
 * Counted from the theme itself: the sections with presets (the ones a
 * merchant can add in the editor), the block types of the product information
 * section, the offer blocks of the product hero, the cart drawer's reward bar,
 * the discount popup's three styles and the storefront locales. Change the
 * theme, recount these.
 */
export const THEME_FEATURES: [string, string][] = [
  ['63 ready-made sections', '63 hazır bölüm'],
  ['21 product info blocks', '21 ürün bilgisi bloğu'],
  ['Quantity breaks and bundles', 'Adet indirimleri ve paketler'],
  ['Cart drawer with a reward bar', 'Ödül çubuklu sepet çekmecesi'],
  ['Discount, spin-wheel and scratch popups', 'İndirim, çark ve kazı-kazan pencereleri'],
  ['Storefront text in 31 languages', '31 dilde mağaza metinleri'],
];

/**
 * The Krevya Theme card, one markup for the landing's pricing and the theme
 * licence page. What changes between them is only what the theme costs there
 * and what the button does: on the landing it is free with a plan and the
 * button starts a signup; on the licence page it is bought outright, or struck
 * through for a workspace that already has it.
 */
export function ThemeCard({
  tx,
  tag,
  price,
  value,
  action,
}: {
  tx: Tx;
  /** The pill beside the name. */
  tag: string;
  /** The price row: a figure in <strong>, a note in <span>. */
  price: ReactNode;
  /** The line under the price; null leaves it out. */
  value: ReactNode;
  /** The button, or the note that replaces it. */
  action: ReactNode;
}) {
  return (
    <article className="lp-theme" aria-labelledby="lp-theme-name">
      <div className="lp-theme-copy">
        <div className="lp-plan-head">
          <h3 id="lp-theme-name">Krevya Theme</h3>
          <span className="lp-plan-tag">{tag}</span>
        </div>
        <p className="lp-theme-price">{price}</p>
        {value ? (
          <p className="lp-theme-value" data-money="">
            {value}
          </p>
        ) : null}
        <p className="lp-theme-for">
          {tx(
            'Built for conversion, made to work with Krevya’s pages.',
            'Dönüşüm için kurgulanmış, Krevya sayfalarıyla birlikte çalışır.',
          )}
        </p>
        <ul className="lp-theme-list">
          {THEME_FEATURES.map(([en, trk]) => (
            <li key={en}>
              <LandingIcon name="check" />
              {tx(en, trk)}
            </li>
          ))}
        </ul>
        {action}
      </div>
      <ThemeArt />
    </article>
  );
}
